import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import DataTable, { Column } from "../components/DataTable";
import StatTile from "../components/StatTile";
import Avatar from "../components/Avatar";
import { PlanPill, StatePill, TagChip } from "../components/Pills";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { fetchCustomers, fetchOverview } from "../dashboardApi";
import { CustomerListResponse, CustomerRow, OverviewResponse, DEFAULT_RANGE } from "../types";
import { colors, type } from "../styles";
import { formatCents, formatDate, formatNumber, formatRelative } from "../format";
import { dashboardPath } from "../routes";
import CustomerFilters, { CustomerFilterState } from "./customers/CustomerFilters";
import CustomerLeaderboard from "./customers/CustomerLeaderboard";
import { useIsNetBasis } from "../revenueBasis";

const splitParam = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

/**
 * Muted em dash + tooltip for a value that genuinely cannot be known — the
 * rollup hasn't reached this customer, or Twilio pools their traffic with
 * someone else's. Deliberately never rendered as 0: "not measurable" and
 * "measured, and it was zero" are different answers.
 */
const PendingCell = ({ reason }: { reason: string }) => (
  <span style={{ color: colors.inkFaint }} title={reason}>
    —
  </span>
);

const CustomersScreen = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNet = useIsNetBasis();

  const filters: CustomerFilterState = useMemo(
    () => ({
      query: searchParams.get("q") ?? "",
      plans: splitParam(searchParams.get("plan")),
      states: splitParam(searchParams.get("state")),
      connect: splitParam(searchParams.get("connect")),
      cancelingOnly: searchParams.get("canceling") === "1",
    }),
    [searchParams]
  );

  const setFilters = useCallback(
    (next: CustomerFilterState) => {
      const params = new URLSearchParams();
      if (next.query) params.set("q", next.query);
      if (next.plans.length) params.set("plan", next.plans.join(","));
      if (next.states.length) params.set("state", next.states.join(","));
      if (next.connect.length) params.set("connect", next.connect.join(","));
      if (next.cancelingOnly) params.set("canceling", "1");
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const listFetcher = useCallback(() => fetchCustomers(), []);
  const list = useDashboardData<CustomerListResponse>(listFetcher, []);

  // The KPI row reuses the Home tab's live monetization figures rather than
  // recomputing them from the fetched rows — one definition, two places.
  const overviewFetcher = useCallback(() => fetchOverview(DEFAULT_RANGE), []);
  const overview = useDashboardData<OverviewResponse>(overviewFetcher, []);

  // Memoized so the filter pass below doesn't re-run on every unrelated render
  // just because `?? []` minted a fresh array.
  const customers = useMemo(() => list.data?.customers ?? [], [list.data]);

  const filtered = useMemo(() => {
    const needle = filters.query.trim().toLowerCase();
    return customers.filter(row => {
      if (needle) {
        const haystack = `${row.fullName} ${row.username} ${row.email}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filters.plans.length && !filters.plans.includes(row.plan)) return false;
      if (filters.states.length && !filters.states.includes(row.subscriptionState)) return false;
      if (filters.connect.length && !filters.connect.includes(row.stripeAccountStatus)) {
        return false;
      }
      if (filters.cancelingOnly && !row.cancelAtEnd) return false;
      return true;
    });
  }, [customers, filters]);

  const columns: Column<CustomerRow>[] = [
    {
      key: "customer",
      label: "Customer",
      sortValue: row => row.fullName?.toLowerCase() ?? "",
      render: row => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            url={row.profilePicURL}
            fullName={row.fullName}
            username={row.username}
            size={28}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ ...type.bodyMedium, color: colors.ink }}>{row.fullName}</span>
            <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
              @{row.username}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "plan",
      label: "Plan",
      sortValue: row => row.plan,
      render: row => <PlanPill plan={row.plan} />,
    },
    {
      key: "status",
      label: "Status",
      sortValue: row => row.subscriptionState,
      render: row => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StatePill state={row.subscriptionState} />
          {row.cancelAtEnd && (
            <TagChip
              label="Canceling"
              tone="warning"
              title="Cancels at the end of the current period"
            />
          )}
        </div>
      ),
    },
    {
      key: "since",
      label: "Customer since",
      sortValue: row => row.subscriptionStart || "",
      render: row => (
        <span title={formatDate(row.subscriptionStart)}>
          {formatRelative(row.subscriptionStart)}
        </span>
      ),
    },
    {
      key: "renews",
      label: "Renews / trial ends",
      sortValue: row => row.subscriptionEnd || "",
      render: row => (
        <span title={formatDate(row.subscriptionEnd)}>
          {formatRelative(row.subscriptionEnd)}
        </span>
      ),
    },
    {
      key: "connect",
      label: "Connect",
      sortValue: row => row.stripeAccountStatus ?? "",
      render: row => (
        <span style={{ ...type.bodyRegular, color: colors.inkMuted, textTransform: "capitalize" }}>
          {row.stripeAccountStatus || "—"}
        </span>
      ),
    },
    {
      key: "followers",
      label: "Followers",
      numeric: true,
      sortValue: row => row.followerCount,
      render: row => formatNumber(row.followerCount),
    },
    {
      key: "texts",
      label: "Texts (30d)",
      numeric: true,
      // Joined server-side from the nightly rollup, batched for the whole page.
      // Sorts unmeasurable rows to the bottom rather than treating them as 0.
      sortValue: row => row.messagesSent30d ?? -1,
      render: row =>
        row.messagingSource === "sharedMainAccount" ? (
          <PendingCell reason="This creator has no Markit number of their own — free tier or not yet approved — so they send on Markit's shared Twilio number, which Twilio cannot break down per creator." />
        ) : row.messagingSource === "unavailable" ? (
          <PendingCell reason="This creator's Markit number could not be resolved to a single owner, so crediting them with any of its traffic would be inventing a split." />
        ) : row.messagesSent30d === null ? (
          <PendingCell reason="The nightly rollup hasn't recorded this customer yet." />
        ) : (
          formatNumber(row.messagesSent30d)
        ),
    },
    {
      key: "revenue",
      label: isNet ? "Markit revenue (net)" : "Markit revenue",
      numeric: true,
      // Sorting follows the displayed figure, so the column a reader sorts by
      // is the column they are looking at. `-1` keeps "not measured" at the
      // bottom of a descending sort rather than mixing it with real zeros.
      sortValue: row =>
        (isNet ? row.lifetimeNetRevenueCents : row.lifetimeRevenueCents) ?? -1,
      render: row => {
        const value = isNet ? row.lifetimeNetRevenueCents : row.lifetimeRevenueCents;
        return value === null ? (
          <PendingCell
            reason={
              isNet
                ? "Stripe's cut was never recorded for this customer, so a net figure cannot be shown. Historical fee backfill is pending."
                : "The nightly rollup hasn't recorded this customer yet."
            }
          />
        ) : (
          formatCents(value)
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Everyone with an active or trialing subscription."
      />

      {(list.error || overview.error) && (
        <ErrorBanner message={list.error ?? overview.error!} />
      )}

      {overview.data && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label="Paying customers"
              value={formatNumber(overview.data.monetization.payingCustomers)}
              freshness={{ kind: "live" }}
            />
            <StatTile
              label="Trials"
              value={formatNumber(overview.data.monetization.trials)}
              freshness={{ kind: "live" }}
            />
            <StatTile
              label="Canceling"
              value={formatNumber(overview.data.monetization.cancelAtEnd)}
              tone={overview.data.monetization.cancelAtEnd > 0 ? "warning" : "neutral"}
              freshness={{ kind: "live" }}
            />
            <StatTile
              label="Payment failed"
              value={formatNumber(overview.data.monetization.paymentFailed)}
              tone={overview.data.monetization.paymentFailed > 0 ? "critical" : "neutral"}
              freshness={{ kind: "live" }}
            />
            {/*
              Same figure and the same two-clock caveat as the Home tab's hero:
              plan baselines live, plus projected overflow from the nightly
              rollup. It wears the rollup's chip for that reason — the two tiles
              must never disagree about what this number is or how fresh it is.
            */}
            <StatTile
              label={isNet ? "Est. MRR (net)" : "Est. MRR"}
              value={
                !isNet
                  ? formatCents(overview.data.monetization.estimatedMrrCents)
                  : overview.data.monetization.estimatedNetMrrCents === null
                  ? "—"
                  : formatCents(overview.data.monetization.estimatedNetMrrCents)
              }
              secondary={
                isNet
                  ? overview.data.monetization.stripeFee.rate === null
                    ? "No realized subscription revenue to measure a Stripe rate from"
                    : `Less Markit's realized ${(
                        overview.data.monetization.stripeFee.rate * 100
                      ).toFixed(1)}% Stripe rate`
                  : "Plan baselines + projected overflow, excludes trials"
              }
              title={
                isNet
                  ? "MRR is a projection of revenue not yet billed, so there is no actual Stripe fee to read. This applies Markit's own realized subscription Stripe rate, measured over the days that have a recorded fee. See the Home tab for the full definition."
                  : "Baseline plan list prices read live, plus a blend of the last 30 and 90 days' realized overflow. See the Home tab for the full definition."
              }
              freshness={{
                kind: "cached",
                updatedAt:
                  overview.data.monetization.overflow.sync.lastRunFinishedAt ?? undefined,
              }}
            />
          </CardGrid>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <CustomerLeaderboard />
      </div>

      <CustomerFilters
        filters={filters}
        onChange={setFilters}
        resultCount={filtered.length}
        totalCount={customers.length}
      />

      {list.loading && <LoadingNote />}

      {list.data && (
        <>
          {list.data.truncated && (
            <div style={{ marginBottom: 16 }}>
              <NoteCard title="List truncated">
                Showing the first {list.data.limit} customers. The endpoint has hit its
                page ceiling and needs real pagination — rows beyond {list.data.limit}{" "}
                are not on this page, and the filters above only search what is.
              </NoteCard>
            </div>
          )}
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={row => row.uid}
            onRowClick={row => navigate(dashboardPath.customer(row.uid))}
            initialSort={{ key: "since", direction: "desc" }}
            emptyLabel={
              customers.length === 0
                ? "No active or trialing customers found."
                : "No customers match these filters."
            }
          />
        </>
      )}
    </div>
  );
};

export default CustomersScreen;
